import {Suspense, useEffect, useState} from 'react'
import {Container, Row, Col, Nav} from 'react-bootstrap'
import {useParams, useSearchParams} from 'react-router-dom'
import {useAuth} from '../../../modules/auth'
import {VISSettingsForm} from './VISSettingsForm'
import {OPISettingsForm} from './OPISettingsForm'
import {InPersonInterpretationSettingsForm} from './InPersonInterpretationSettingsForm'
import {CustomerExpensesView} from '../CustomerExpensesView'
import {RoutingView} from '../routing/RoutingView'
import {QuickDialsView} from '../quick-dials/QuickDialsView'
import {AIInterpretationSettingsView} from './AIInterpretationSettingsView'
import {CustomerMfaSettingsView} from './CustomerMfaSettingsView'
import {CustomerReportingSettingsView} from './CustomerReportingSettingsView'
import {AiAssignmentAgentSettingsForm} from './AiAssignmentAgentSettingsForm'
import {useAiAssignmentFeatureEnabled} from '../../../hooks/useAiAssignmentFeature'

type SettingsSection =
  | 'vis'
  | 'opi'
  | 'ipi'
  | 'billing-expense'
  | 'routing'
  | 'quick-dials'
  | 'ai-interpretation'
  | 'mfa'
  | 'reporting'
  | 'ai-assignment-agent'

const SECTION_LABELS: Record<SettingsSection, string> = {
  vis: 'Meeting Rooms (VIS)',
  opi: 'OPI',
  ipi: 'In-Person Interpretation',
  'billing-expense': 'Billing Expense',
  routing: 'Routing',
  'quick-dials': 'Quick Dials',
  'ai-interpretation': 'AI Interpretation',
  mfa: 'Multi Factor Authentication',
  reporting: 'Reporting',
  'ai-assignment-agent': 'AI Assignment Agent',
}

const VALID_SECTIONS: SettingsSection[] = [
  'vis',
  'opi',
  'ipi',
  'billing-expense',
  'routing',
  'quick-dials',
  'ai-interpretation',
  'mfa',
  'reporting',
  'ai-assignment-agent',
]

function isValidSection(value: string | null): value is SettingsSection {
  return !!value && VALID_SECTIONS.includes(value as SettingsSection)
}

export function AppointmentsSettingsView() {
  const {id} = useParams()
  const customerId = parseInt(id || '0', 10)
  const {currentUser} = useAuth()
  const isSystemUser = currentUser?.result?.userType === 'SYSTEM'
  const aiAssignmentEnabled = useAiAssignmentFeatureEnabled(isSystemUser)
  const [searchParams, setSearchParams] = useSearchParams()
  const sectionParam = searchParams.get('section')

  const [active, setActive] = useState<SettingsSection>(() =>
    isValidSection(sectionParam) ? sectionParam : 'vis'
  )

  useEffect(() => {
    if (isValidSection(sectionParam)) {
      setActive(sectionParam)
    }
  }, [sectionParam])

  const handleSectionClick = (key: SettingsSection) => {
    setActive(key)
    const next = new URLSearchParams(searchParams)
    next.set('tab', '19')
    next.set('section', key)
    setSearchParams(next, {replace: true})
  }

  const sidebarItem = (key: SettingsSection) => {
    const isActive = active === key
    return (
      <div
        role='button'
        onClick={() => handleSectionClick(key)}
        onKeyDown={() => handleSectionClick(key)}
        tabIndex={0}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '14px 16px',
          cursor: 'pointer',
          background: isActive ? '#e53935' : 'transparent',
          color: isActive ? '#fff' : '#333',
          borderRadius: 6,
          marginBottom: 8,
        }}
      >
        <div
          style={{
            width: 6,
            height: '100%',
            background: isActive ? '#b71c1c' : 'transparent',
            borderRadius: '0 3px 3px 0',
            marginRight: 12,
          }}
        />
        <div style={{fontWeight: 600}}>{SECTION_LABELS[key]}</div>
      </div>
    )
  }

  const visibleSections: SettingsSection[] = [
    'vis',
    'opi',
    'ipi',
    'billing-expense',
    ...(currentUser?.result?.userType === 'SYSTEM' ? (['routing'] as const) : []),
    'quick-dials',
    'ai-interpretation',
    'mfa',
    ...(isSystemUser ? (['reporting'] as const) : []),
    ...(isSystemUser && aiAssignmentEnabled ? (['ai-assignment-agent'] as const) : []),
  ]

  const renderSectionContent = () => {
    switch (active) {
      case 'vis':
        return <VISSettingsForm customerId={customerId} />
      case 'opi':
        return <OPISettingsForm customerId={customerId} />
      case 'ipi':
        return <InPersonInterpretationSettingsForm customerId={customerId} />
      case 'billing-expense':
        return <CustomerExpensesView customerCode={id || '0'} />
      case 'routing':
        return <RoutingView />
      case 'quick-dials':
        return <QuickDialsView />
      case 'ai-interpretation':
        return <AIInterpretationSettingsView customerId={customerId} />
      case 'mfa':
        return <CustomerMfaSettingsView customerId={customerId} />
      case 'reporting':
        return <CustomerReportingSettingsView customerId={customerId} />
      case 'ai-assignment-agent':
        return aiAssignmentEnabled ? <AiAssignmentAgentSettingsForm customerId={customerId} /> : null
      default:
        return null
    }
  }

  return (
    <Container fluid className='p-3'>
      <Row className='align-items-center mb-3'>
        <Col>
          <h3>Settings</h3>
        </Col>
      </Row>

      <Row>
        <Col md={3} lg={3} className='pe-4'>
          <div style={{maxWidth: 320}}>
            <Nav className='flex-column'>
              {visibleSections.map((key) => (
                <Nav.Item key={key}>{sidebarItem(key)}</Nav.Item>
              ))}
            </Nav>
          </div>
        </Col>

        <Col md={9} lg={9}>
          <div style={{background: '#fff', padding: 24, borderRadius: 8, minHeight: 320}}>
            <Suspense fallback={<div>Loading Settings...</div>}>
              {renderSectionContent()}
            </Suspense>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
